import React, { useState, useEffect } from "react";
import FahrasUsersTable from "./FahrasUsersTable";
import { AddUserDialog, EditUserDialog } from "./FahrasUsersDialogs";
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  fetchRoles,
} from "./FahrasUsersAPI";
import PageLayout from "../../components/PageLayout";
import SearchToolbar from "../../components/SearchToolbar";
import usePageTitle from "../../services/usePageTitle";
import usePageState from "../../hooks/usePageState";
import useIsRoleUser from "../../hooks/useIsRoleUser";
import { extractMsg } from "../../utils/errorUtils";
import fahrasUsersHelp from "../../content/help/fahras-users.json";

export default function FahrasUsers({ pageTitle = "Fahras Users" }) {
  usePageTitle("Fahras Users - SkeyeGate™");

  const {
    filteredData,
    loading,
    searchText,
    page,
    pageSize,
    messageApi,
    contextHolder,
    handleSearch,
    clearFilter,
    loadData,
    onPageChange,
  } = usePageState({
    fetchFn: fetchUsers,
    filterFields: [
      "name",
      "displayName",
      "email",
      (u) => (u.isAdmin ? "admin" : u.isBot ? "bot" : "user"),
    ],
  });

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const isRoleUser = useIsRoleUser();
  const [rolesList, setRolesList] = useState([]);

  useEffect(() => {
    fetchRoles()
      .then(setRolesList)
      .catch((err) => console.error("Failed to fetch roles:", err));
  }, []);

  const handleAddUser = async (values) => {
    try {
      await createUser({
        name: values.name,
        displayName: values.displayName || values.name,
        email: values.email,
        password: values.password,
        isAdmin: values.isAdmin || false,
        isBot: false,
        roles: values.roles || [],
      });
      messageApi.success("User created successfully");
      loadData();
    } catch (error) {
      throw error;
    }
  };

  const handleEditUser = async (values) => {
    try {
      await updateUser({
        name: values.name,
        displayName: values.displayName,
        email: values.email,
        isAdmin: values.isAdmin || false,
        isBot: userToEdit?.isBot || false,
        ...(values.password ? { password: values.password } : {}),
      });
      messageApi.success("User updated successfully");
      loadData();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId);
      messageApi.success("User deleted successfully.");
      loadData();
    } catch (error) {
      messageApi.error({
        content: extractMsg(error, "Failed to delete user"),
        key: "delete-user-error",
      });
    }
  };

  return (
    <div className="fahras-users-page">
      {contextHolder}
      <PageLayout
        title={pageTitle}
        logoSrc="/img/Fahras_notext.png"
        description="Manager for Fahras users, allowing you to create, edit, and organize your user accounts and permissions in one place."
        helpContent={fahrasUsersHelp}
      >
        <SearchToolbar
          search={searchText}
          onSearchChange={handleSearch}
          onClearFilter={clearFilter}
          addButtonLabel="Add User"
          onAdd={() => setAddModalOpen(true)}
          showAdd={!isRoleUser}
        />

        <FahrasUsersTable
          data={filteredData}
          loading={loading}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onEditUser={(user) => {
            setUserToEdit(user);
            setEditModalOpen(true);
          }}
          onDeleteUser={handleDeleteUser}
          isRoleUser={isRoleUser}
        />
      </PageLayout>

      <AddUserDialog
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddUser}
        rolesList={rolesList}
      />
      <EditUserDialog
        open={editModalOpen}
        user={userToEdit}
        onClose={() => setEditModalOpen(false)}
        onSave={handleEditUser}
      />
    </div>
  );
}
